import { useEffect, useState } from 'react';
import { Layout, type Tab } from './components/Layout';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { AdminPanels } from './components/AdminPanels';
import { InventoryPanels } from './components/InventoryPanels';
import { LicenseAssignment } from './components/LicenseAssignment';
import { InventoryPull } from './components/InventoryPull';
import { DomainLookup } from './components/DomainLookup';
import { Orders } from './components/Orders';
import { Settings } from './components/Settings';
import { Transformation } from './components/Transformation';
import { TransformationRun } from './components/TransformationRun';
import { OAuthApps } from './components/OAuthApps';
import { DomainAddRuns } from './components/DomainAddRuns';
import { DomainSwapRuns } from './components/DomainSwapRuns';
import { ForeignDnsRuns } from './components/ForeignDnsRuns';
import { DomainContests } from './components/DomainContests';
import { SequencerConnect } from './components/SequencerConnect';
import { MasterInboxForwarding } from './components/MasterInboxForwarding';
import { DomainInventory } from './components/DomainInventory';
import { DomainRemoval } from './components/DomainRemoval';
import { UserRemoval } from './components/UserRemoval';
import { AutoRemovals } from './components/AutoRemovals';
import { RecoverDeletedUsers } from './components/RecoverDeletedUsers';
import { SerkenRenewalMigration } from './components/SerkenRenewalMigration';
import { Px3 } from './components/Px3';
import { BuildLine } from './components/BuildLine/BuildLine';
import { Monitoring } from './components/Monitoring';
import { TeamApp } from './team/TeamApp';
import { TeamLeadApp } from './teamlead/TeamLeadApp';

function App(): JSX.Element | null {
  // Same React bundle, three top-level surfaces:
  //   /teamlead  - team-lead password gate (Inventory, Licensing, Lookup, OAuth, Master Inbox, Foreign CF DNS, Sequencer Connect)
  //   /team      - team password gate (Licensing + Foreign CF DNS + Sequencer Connect)
  //   anything else — admin app
  // Order matters: /teamlead must be checked BEFORE /team because /team is a prefix.
  const path = window.location.pathname;
  if (path.startsWith('/teamlead')) {
    return <TeamLeadApp />;
  }
  if (path.startsWith('/team')) {
    return <TeamApp />;
  }
  return <AdminApp />;
}

function AdminApp(): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return null;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <Layout
      activeTab={activeTab}
      onTabChange={(tab: Tab) => {
        setOpenRunId(null);
        setActiveTab(tab);
      }}
    >
      {activeTab === 'dashboard' && (
        <Dashboard
          onNavigate={(tab) => {
            setOpenRunId(null);
            setActiveTab(tab);
          }}
        />
      )}
      {activeTab === 'admin-panels' && <AdminPanels />}
      {activeTab === 'inventory-panels' && <InventoryPanels />}
      {activeTab === 'orders' && <Orders />}
      {activeTab === 'license-assignment' && <LicenseAssignment />}
      {activeTab === 'inventory-pull' && <InventoryPull />}
      {activeTab === 'domain-lookup' && <DomainLookup />}
      {activeTab === 'transformation' &&
        (openRunId ? (
          <TransformationRun runId={openRunId} onBack={() => setOpenRunId(null)} />
        ) : (
          <Transformation onOpenRun={(id: string) => setOpenRunId(id)} />
        ))}
      {activeTab === 'oauth-apps' && <OAuthApps />}
      {activeTab === 'domain-add' && <DomainAddRuns />}
      {activeTab === 'domain-swap' && <DomainSwapRuns />}
      {activeTab === 'foreign-dns' && <ForeignDnsRuns />}
      {activeTab === 'domain-contests' && <DomainContests />}
      {activeTab === 'sequencer-connect' && <SequencerConnect />}
      {activeTab === 'master-inbox' && <MasterInboxForwarding />}
      {activeTab === 'domain-inventory' && <DomainInventory />}
      {activeTab === 'domain-removal' && <DomainRemoval />}
      {activeTab === 'user-removal' && <UserRemoval />}
      {activeTab === 'auto-removals' && <AutoRemovals />}
      {activeTab === 'recover-deleted-users' && <RecoverDeletedUsers />}
      {activeTab === 'serken-migration' && <SerkenRenewalMigration />}
      {activeTab === 'px3' && <Px3 />}
      {activeTab === 'build-line' && <BuildLine />}
      {activeTab === 'monitoring-gmail' && <Monitoring kind="gmail" />}
      {activeTab === 'monitoring-health' && <Monitoring kind="health" />}
      {activeTab === 'settings' && <Settings />}
    </Layout>
  );
}

export default App;
